import {CustomDomainCrud} from './api';
import {CustomDomain, CustomDomainRequest, CustomDomainState} from '../../types/organization/CustomDomain';
import {StorageType} from '../../types/organization/StorageSource';
import {resolveWithRandomDelay} from '../storageSource/shim';

export class CrudShim implements CustomDomainCrud {
    orgId: string;
    private myList: CustomDomain[];

    constructor(orgId: string) {
        this.orgId = orgId;
        this.myList = [
            {
                hostname: 'example.com',
                type: 1,
                azure_cdn_endpoint: {
                    name: 'pxl01',
                    hostname: 'pxl01.azureedge.net',
                },
                state: CustomDomainState.DOMAIN_VERIFIED,
                metadata: {},
                organization_storage_source: null,
                enable_media_urls: false,
            },
            {
                hostname: 'example2.com',
                type: 1,
                azure_cdn_endpoint: {
                    name: 'pxl02',
                    hostname: 'pxl02.azureedge.net',
                },
                state: CustomDomainState.CNAME_VERIFIED,
                metadata: {},
                organization_storage_source: null,
                enable_media_urls: false,
            },
            {
                hostname: 'example3.com',
                type: 1,
                azure_cdn_endpoint: {
                    name: 'pxl03',
                    hostname: 'pxl03.azureedge.net',
                },
                state: CustomDomainState.CNAME_PENDING,
                metadata: {},
                organization_storage_source: null,
                enable_media_urls: false,
            },
            {
                hostname: 'example4.com',
                type: 1,
                azure_cdn_endpoint: {
                    name: 'pxl04',
                    hostname: 'pxl04.azureedge.net',
                },
                state: CustomDomainState.DOMAIN_READY,
                metadata: {},
                organization_storage_source: {
                    name: 'RemoteStorage',
                    type: StorageType.REMOTE_STORAGE,
                    origin: 'https://remote.com',
                    headers: [],
                    seo_enabled: true,
                },
                enable_media_urls: false,
            },
            {
                hostname: 'example5.com',
                type: 1,
                azure_cdn_endpoint: {
                    name: 'pxl05',
                    hostname: 'pxl05.azureedge.net',
                },
                state: CustomDomainState.DOMAIN_ERROR,
                metadata: {
                    error: 'Something went wrong. Please contact support.',
                },
                organization_storage_source: null,
                enable_media_urls: false,
            },
        ];
    }

    async list() {
        const response: CustomDomain[] = [...this.myList];
        return resolveWithRandomDelay(response);
    }

    async get(hostname: string) {
        const domain = this.myList.find((d) => d.hostname === hostname);
        if (!domain) {
            throw new Error('Domain not found');
        }
        return resolveWithRandomDelay(domain);
    }

    async add(src: CustomDomainRequest) {
        let storage_type: number;
        switch (src.org_storage_source) {
            case 'DefaultStorage':
                storage_type = 4;
                break;
            case 'RemoteStorage':
            default:
                storage_type = 5;
        }
        const newDomain: CustomDomain = {
            hostname: src.hostname,
            type: 1,
            azure_cdn_endpoint: {
                name: 'pxl01',
                hostname: 'pxl01.azureedge.net',
            },
            state: CustomDomainState.CNAME_PENDING,
            metadata: {},
            organization_storage_source:
                src.org_storage_source !== undefined
                    ? {
                          type: storage_type,
                          name: src.org_storage_source,
                          origin: 'https://remote.com',
                          headers: [],
                          seo_enabled: true,
                      }
                    : null,
            enable_media_urls: src.enable_media_urls ?? false,
        };
        this.myList = [...this.myList, newDomain];
        return resolveWithRandomDelay(newDomain);
    }

    async verify(hostname: string) {
        const domain = this.myList.find((d) => d.hostname === hostname);
        if (!domain) {
            throw new Error('Domain not found');
        }
        const response: CustomDomain = {...domain};
        response.state = CustomDomainState.DOMAIN_PENDING;
        return resolveWithRandomDelay(response);
    }

    async update(src: CustomDomainRequest) {
        const domain = this.myList.find((d) => d.hostname === src.hostname);
        if (!domain) {
            throw new Error('Domain not found');
        }
        const response: CustomDomain = {...domain};
        let storage_type: number;
        switch (src.org_storage_source) {
            case 'DefaultStorage':
                storage_type = 4;
                break;
            case 'RemoteStorage':
            default:
                storage_type = 5;
        }
        response.organization_storage_source = {
            type: storage_type,
            name: src.org_storage_source ?? 'RemoteStorage',
            origin: 'https://remote.com',
            headers: [],
            seo_enabled: true,
        };
        response.enable_media_urls = src.enable_media_urls ?? false;
        return resolveWithRandomDelay(response);
    }

    async delete(hostname: string) {
        this.myList = this.myList.filter((d) => d.hostname !== hostname);
        return resolveWithRandomDelay(undefined);
    }
}
